/**
 * @fileoverview Invitation row actions hook.
 * @description What a User Management row does with a Pending Invitation user's invitation:
 * Resend it by the id the directory carries, or Revoke it, which first finds the user's manageable
 * invitation (the directory lists users, not invitations) and then holds it for the Revoke dialog.
 * It reports the outcomes to the page through callbacks, so the page owns what is shown.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

import { useResendInvitation } from '@/features/user-invitations/hooks/useResendInvitation'
import { useFindUserInvitation } from '@/features/user-invitations/hooks/useUserInvitationActions'

import type { InvitationSummary } from '@/features/user-invitations/types/user-invitations.types'

interface InvitationRowActionsOptions {
  /** Called as an action starts, to clear the previous action's error. */
  onStart: () => void
  /** Called with the invited user's name once their invitation was re-sent. */
  onResent: (name: string) => void
  /** Called with the refusal's message when an action failed. */
  onFailed: (message: string) => void
  /** Called with the user's name when they have no invitation to revoke. */
  onNotFound: (name: string) => void
}

/** The directory user an invitation action is for. */
interface InvitedUserRow {
  userId: string
  email: string
  displayName: string
}

/**
 * @description Runs Resend and Revoke for a directory user's invitation.
 * @param {InvitationRowActionsOptions} options - The callbacks that report each outcome.
 * @returns {object} `resend`, `startRevoke`, the invitation the Revoke dialog is open for, and
 * `closeRevoke`.
 */
export function useUserInvitationRowActions({
  onStart,
  onResent,
  onFailed,
  onNotFound,
}: InvitationRowActionsOptions) {
  const resendInvitation = useResendInvitation()
  const findUserInvitation = useFindUserInvitation()
  const [revokingInvitation, setRevokingInvitation] = useState<InvitationSummary | null>(null)

  /**
   * @description Re-sends a Pending Invitation user's invitation by its id.
   * @param {string} invitationId - The user's pending invitation.
   * @param {string} name - The invited user's name, for the confirmation.
   */
  const resend = (invitationId: string, name: string) => {
    onStart()
    resendInvitation.mutate(invitationId, {
      /**
       * @description Confirms the invitation was re-sent.
       */
      onSuccess: () => {
        onResent(name)
      },
      /**
       * @description Reports why the invitation could not be re-sent.
       * @param {ApiError} error - The refusal.
       */
      onError: (error) => {
        onFailed(error.message)
      },
    })
  }

  /**
   * @description Finds a Pending Invitation user's invitation and holds it for the Revoke dialog.
   * @param {InvitedUserRow} target - The invited user.
   */
  const startRevoke = (target: InvitedUserRow) => {
    onStart()
    findUserInvitation.mutate(
      { userId: target.userId, email: target.email },
      {
        /**
         * @description Opens the Revoke dialog for the invitation found, or says there is none.
         * @param {InvitationSummary | null} invitation - The user's invitation, if one was found.
         */
        onSuccess: (invitation) => {
          if (invitation) {
            setRevokingInvitation(invitation)
          } else {
            onNotFound(target.displayName)
          }
        },
        /**
         * @description Reports why the invitation could not be looked up.
         * @param {ApiError} error - The refusal.
         */
        onError: (error) => {
          onFailed(error.message)
        },
      },
    )
  }

  /**
   * @description Closes the Revoke dialog.
   */
  const closeRevoke = () => {
    setRevokingInvitation(null)
  }

  return { resend, startRevoke, revokingInvitation, closeRevoke }
}
